import jwtDecode from 'jwt-decode'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useSearchParams } from 'react-router-dom'

import { isEmbedded } from '@yes.technology/react-toolkit'
import { defaultLanguage } from 'infrastructure/i18n'
import { useLocalStorage, useSessionStorage } from 'shared/hooks'
import { Credentials } from '../types/authentication.type'

const useRedirectOrigin = () => {
  const [autoRedirect, setAutoRedirect] = useState(false)
  const [authenticatedData] = useLocalStorage('auth')
  const [searchParams, setSearchParams] = useSearchParams()
  const { language } = useTranslation()

  const [redirectOrigin, setRedirectOrigin] = useSessionStorage(
    'redirectOrigin',
    window.fallbackOrigin
  )

  const handleRedirectOrigin = useCallback(() => {
    if (searchParams.get('redirectOrigin')) {
      setRedirectOrigin(searchParams.get('redirectOrigin') || '')
      searchParams.delete('redirectOrigin')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, setRedirectOrigin])

  useEffect(handleRedirectOrigin, [handleRedirectOrigin])

  const isCredentialsValid = (credentials: Credentials) =>
    credentials?.applicationsystem_uuid &&
    credentials?.organization_uuid &&
    credentials?.user_uuid

  const token =
    authenticatedData instanceof Object &&
    'token' in authenticatedData &&
    typeof authenticatedData.token === 'string'
      ? authenticatedData.token
      : ''

  const isTokenValid = useCallback(
    (upgradedToken?: string | undefined) => {
      try {
        const decodedToken = jwtDecode<{ credentials: Credentials }>(
          upgradedToken || token
        )
        return isCredentialsValid(decodedToken?.credentials)
      } catch (error) {
        return false
      }
    },
    [token]
  )

  const redirectToOrigin = useCallback(
    (upgradedToken?: string | undefined) => {
      if (!isTokenValid(upgradedToken)) {
        setAutoRedirect(true)
        return
      }

      if (isEmbedded()) {
        window.parent.postMessage(
          {
            token: upgradedToken || token,
            lang: language || defaultLanguage
          },
          '*'
        )

        return
      }

      const urlToRedirectWithParams = buildAuthenticatedRedirectUrl(
        redirectOrigin,
        upgradedToken || token,
        language
      )

      window.location.assign(urlToRedirectWithParams)
    },
    [token, language, redirectOrigin, isTokenValid]
  )

  const handleToken = useCallback(() => {
    if (isTokenValid() && autoRedirect) {
      setAutoRedirect(false)
      redirectToOrigin()
    }
  }, [autoRedirect, isTokenValid, redirectToOrigin])

  useEffect(handleToken, [handleToken])

  return {
    redirectToOrigin
  }
}

function buildAuthenticatedRedirectUrl(
  redirectOrigin: string,
  token: string,
  language: string
): string {
  const redirectOriginURL = new URL(redirectOrigin)
  redirectOriginURL.searchParams.set('token', token)
  redirectOriginURL.searchParams.set('lang', language || defaultLanguage)
  return redirectOriginURL.toString()
}

export default useRedirectOrigin
